import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Play, Square } from 'lucide-react';
import { useVoiceCatalog, voiceLine } from '@/hooks/useVoiceCatalog';
import { useVoiceAnswer } from '@/lib/ivyChat/voiceAnswer';
import { QuestionButton, QuestionCard } from '@/components/studio/plan/Thread';
import { cn } from '@/lib/utils';

export interface VoiceSelectorConfig {
  recommended: string;
  context: string;
  candidates: string[];
}

/**
 * Direction B (beta only): the chat's "Pick a voice" in the question card's language. Each voice is a row: its play
 * button, its name whole with Google's line under it, its selection on the right; the recommended one has one small
 * tag. The pick goes to the chat as before (ivy-voice-selected); the answer lives outside the card (useVoiceAnswer).
 */
export function VoiceCardB({ config }: { config: VoiceSelectorConfig }) {
  const [answer, pick] = useVoiceAnswer(config.recommended);
  const [choice, setSelected] = useState(config.recommended);
  const selected = answer ?? choice;
  const submitted = answer !== null;
  const [playing, setPlaying] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const catalog = useVoiceCatalog();

  const others = config.candidates.filter((n) => n !== config.recommended);
  const extra = [...(catalog?.keys() ?? [])].filter((n) => n !== config.recommended && !others.includes(n));
  const shown = [config.recommended, ...others, ...(showMore ? extra : [])];

  useEffect(() => () => audioRef.current?.pause(), []);

  const play = (name: string) => {
    audioRef.current?.pause();
    if (playing === name) {
      audioRef.current = null;
      setPlaying(null);
      return;
    }
    const url = catalog?.get(name)?.sampleUrl;
    if (!url) return;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.addEventListener('ended', () => setPlaying(null));
    audio.addEventListener('error', () => setPlaying(null));
    audio.play().catch(() => setPlaying(null));
    setPlaying(name);
  };

  const confirm = () => pick(selected);

  return (
    <QuestionCard
      title="Pick a voice"
      facts={config.context || undefined}
      hint={submitted ? <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" />Using {selected}</span> : undefined}
    >
      <div role="radiogroup" aria-label="Voices" className="flex w-full flex-col gap-0.5">
        {shown.map((name) => (
          <VoiceRow
            key={name}
            name={name}
            line={catalog?.get(name) ? lineFor(catalog.get(name)!) : null}
            title={catalog?.get(name)?.persona ? `${catalog.get(name)!.persona}. ${catalog.get(name)!.summary ?? ''}` : undefined}
            recommended={name === config.recommended}
            checked={name === selected}
            playing={playing === name}
            disabled={submitted}
            onPick={() => setSelected(name)}
            onPlay={() => play(name)}
          />
        ))}
      </div>
      {extra.length > 0 && (
        <button type="button" onClick={() => setShowMore(!showMore)} aria-expanded={showMore} className="inline-flex items-center gap-1 text-b-meta text-b-t2 underline-offset-2 hover:text-b-t1 hover:underline">
          {showMore ? 'Fewer voices' : `More voices (${extra.length})`}
          <ChevronDown aria-hidden className={cn('h-3.5 w-3.5 transition-transform', showMore && 'rotate-180')} />
        </button>
      )}
      <div className="w-full">
        <QuestionButton label={`Use ${selected}`} recommended disabled={submitted} onClick={confirm} />
      </div>
    </QuestionCard>
  );
}

const lineFor = (voice: { summary: string; gender?: string }) =>
  [voiceLine(voice), voice.gender === 'female' ? 'Female' : voice.gender === 'male' ? 'Male' : null].filter(Boolean).join(' · ');

function VoiceRow({ name, line, title, recommended, checked, playing, disabled, onPick, onPlay }: {
  name: string; line: string | null; title?: string; recommended: boolean; checked: boolean; playing: boolean; disabled: boolean;
  onPick: () => void; onPlay: () => void;
}) {
  const onKey = (e: KeyboardEvent) => {
    if (disabled || (e.key !== ' ' && e.key !== 'Enter')) return;
    e.preventDefault();
    onPick();
  };
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label={`${playing ? 'Stop' : 'Play'} ${name}`}
        onClick={onPlay}
        className="grid h-8 w-8 flex-none place-items-center rounded-full bg-b-raised text-b-t1 transition-colors hover:bg-b-elevated"
      >
        {playing ? <Square className="h-3 w-3 fill-current" /> : <Play className="ml-px h-3 w-3 fill-current" />}
      </button>
      <div
        role="radio"
        aria-checked={checked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        title={title}
        onClick={() => !disabled && onPick()}
        onKeyDown={onKey}
        className={cn(
          'flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-[var(--b-radius-control)] px-2.5 py-1.5 transition-colors',
          checked ? 'bg-b-raised' : 'hover:bg-b-raised',
          disabled && 'cursor-default opacity-60',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span data-voice-name className="text-b-read font-medium text-b-t1">{name}</span>
            {recommended && (
              <span data-voice-tag className="whitespace-nowrap rounded-full border border-b-line bg-b-panel px-2 py-px text-[11px] leading-4 text-b-t2">Recommended</span>
            )}
          </div>
          {line && <p data-voice-line className="text-b-meta text-b-t2">{line}</p>}
        </div>
        <span aria-hidden className={cn('h-4 w-4 flex-none rounded-full border transition-[border-width]', checked ? 'border-[5px] border-b-ink' : 'border-b-line-strong')} />
      </div>
    </div>
  );
}
